// God → Gateway → ProtonPay account: the one shared ProtonPay account (credentials,
// environment, platform switches, callback IP allowlist) and the notify URLs to give
// ProtonPay. Merchants never see any of this.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Link2, Plug, RefreshCw } from "lucide-react";
import { god } from "../../godApi";
import {
  Button, Callout, Dialog, Empty, Field, Input, Page, Rows, Section, Select, Skeleton, Toggle, inr, when,
} from "../../kit";

export default function GwAccount() {
  const [s, setS] = useState(null);
  const [f, setF] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const [ask, setAsk] = useState(null); // { type: "live" | "rotate", rotate }
  const [err, setErr] = useState("");

  const load = useCallback(() => god.gwSettings().then((d) => { setS(d); setF(d); setErr(""); }).catch((e) => { setErr(e.message); toast.error(e.message); }), []);
  useEffect(() => { load(); }, [load]);

  if (!f) return <Page>{err ? <Callout tone="red" title="Couldn't load the ProtonPay account">{err}</Callout> : <Section><Skeleton rows={8} /></Section>}</Page>;

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e?.target ? e.target.value : e }));

  const save = async (rotate = false, liveOk = false) => {
    if (f.environment === "live" && s.environment !== "live" && !liveOk) {
      setAsk({ type: "live", rotate });
      return;
    }
    setAsk(null);
    setBusy(true);
    try {
      const d = await god.gwSaveSettings({
        merchant_code: f.merchant_code, x_key: key || undefined, base_url: f.base_url, channel: f.channel, callback_ips: f.callback_ips ?? undefined,
        environment: f.environment, payin_enabled: f.payin_enabled, payout_enabled: f.payout_enabled, rotate_notify_token: rotate,
      });
      setS(d); setF(d); setKey("");
      toast.success(rotate ? "Saved — send the new notify URLs to ProtonPay" : "Saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    const r = await god.gwTestSettings().catch((e) => ({ ok: false, error: e.message }));
    setTest(r);
    setTesting(false);
  };

  return (
    <Page>
      <div className="k-split">
        <Section title="ProtonPay account" icon={KeyRound}
          sub={`One account for the whole platform.${s.updated_at ? ` Last saved ${when(s.updated_at, true)}.` : ""}`}>
          <div className="k-form">
            <Field label="Merchant code">
              <Input mono data-testid="god-gw-code" value={f.merchant_code} onChange={set("merchant_code")} placeholder="mc-…" />
            </Field>
            <Field label="X-Key" hint={s.x_key_masked ? `Saved: ${s.x_key_masked}. Leave blank to keep it.` : "Required. Given by ProtonPay."}>
              <Input mono data-testid="god-gw-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off"
                placeholder={s.x_key_masked ? "••••••••" : "Key from ProtonPay"} />
            </Field>
            <Field label="API base URL" wide>
              <Input mono data-testid="god-gw-base" value={f.base_url} onChange={set("base_url")} />
            </Field>
            <Field label="Channel" hint="Given by ProtonPay: ftd on staging, trusted on production.">
              <Input value={f.channel} onChange={set("channel")} placeholder="trusted" />
            </Field>
            <Field label="Environment" hint="Merchants' API key mode must match.">
              <Select data-testid="god-gw-env" value={f.environment} onChange={set("environment")} options={[["test", "Test"], ["live", "Live"]]} />
            </Field>
            <Field label="Callback IPs" wide hint="Comma separated. Only callbacks from these IPs are accepted. Blank = accept any.">
              <Input mono value={f.callback_ips ?? ""} onChange={set("callback_ips")} placeholder="164.52.196.57, 164.52.203.72" />
            </Field>
          </div>

          <div className="gwa-switches">
            <Rows items={[
              ["Payins enabled (platform-wide)", <Toggle label="Payins enabled" on={!!f.payin_enabled} onChange={set("payin_enabled")} />],
              ["Payouts enabled (platform-wide)", <Toggle label="Payouts enabled" on={!!f.payout_enabled} onChange={set("payout_enabled")} />],
            ]} />
          </div>

          {test && (test.ok
            ? <Callout tone="green" title="Connected">ProtonPay payout balance {inr(test.payout_balance)}</Callout>
            : <Callout tone="red" title="Connection failed">{test.error}</Callout>)}

          <div className="k-actions gwa-foot">
            <Button icon={Plug} disabled={busy || testing || !s.configured} onClick={runTest}
              title={s.configured ? "Uses the saved credentials" : "Save credentials first"}>
              {testing ? "Testing…" : "Test connection"}
            </Button>
            <Button variant="primary" data-testid="god-gw-save" disabled={busy || !f.merchant_code} onClick={() => save(false)}>
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </Section>

        <Section title="Give these to ProtonPay" icon={Link2} sub="ProtonPay sets the notify URLs on their side.">
          {s.notify_payin_url ? (
            <div className="gwa-stack">
              <Callout tone="amber" title="These URLs contain a secret">Send them to ProtonPay privately. Don't paste them in shared channels or tickets.</Callout>
              <Rows items={[
                ["Payin notify URL (POST)", s.notify_payin_url, { mono: true, copy: true }],
                ["Payout notify URL (POST)", s.notify_payout_url, { mono: true, copy: true }],
                ["Return URL (sent per payin)", s.return_url, { mono: true, copy: true }],
              ]} />
              {!s.notify_payin_url.startsWith("https://") && (
                <Callout tone="blue">These aren't https URLs. Use the live site's URLs for ProtonPay.</Callout>
              )}
              <div className="k-actions">
                <Button variant="danger" size="sm" icon={RefreshCw} disabled={busy} onClick={() => setAsk({ type: "rotate" })}>Rotate notify secret</Button>
              </div>
            </div>
          ) : (
            <Empty icon={Link2} title="No URLs yet">Save the credentials to generate them.</Empty>
          )}
        </Section>
      </div>

      {ask?.type === "live" && (
        <Dialog title="Switch the gateway to live?" onClose={() => setAsk(null)} footer={<>
          <Button onClick={() => setAsk(null)}>Cancel</Button>
          <Button variant="primary" disabled={busy} onClick={() => save(ask.rotate, true)}>Switch to live</Button>
        </>}>
          <Callout tone="red" title="Real money will move">Merchants must use sk_live_ keys once the gateway is live.</Callout>
        </Dialog>
      )}

      {ask?.type === "rotate" && (
        <Dialog title="Rotate the notify secret?" onClose={() => setAsk(null)} footer={<>
          <Button onClick={() => setAsk(null)}>Cancel</Button>
          <Button variant="danger" disabled={busy} onClick={() => save(true)}>Rotate secret</Button>
        </>}>
          <Callout tone="amber" title="Callbacks to the old URLs stop working">
            ProtonPay's callbacks fail until they update to the new notify URLs. Any unsaved changes in the form are saved too.
          </Callout>
        </Dialog>
      )}

      <style>{`
        .gwa-switches { margin-top: 14px; padding-top: 4px; border-top: 1px solid #F0F2F6; }
        .gwa-foot { margin-top: 14px; }
        .gwa-stack { display: flex; flex-direction: column; gap: 12px; }
        .gwa-switches + .k-callout { margin-top: 12px; }
      `}</style>
    </Page>
  );
}
